import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchSprintsByProject, SprintStatus } from '../../../store/sprintSlice';
import { featureAPI } from '../../../api/services';
import api from '../../../api/axios';

interface Props {
  projectId: string;
  onSelectSprint?: (sprint: any) => void;
  selectedSprintId?: string | null;
}

const statusColors: Record<SprintStatus, string> = {
  planned: 'bg-gray-400', active: 'bg-green-500', paused: 'bg-yellow-500',
  completed: 'bg-blue-500', cancelled: 'bg-red-400',
};

const SprintTree: React.FC<Props> = ({ projectId, onSelectSprint, selectedSprintId }) => {
  const dispatch = useAppDispatch();
  const { sprints, loading } = useAppSelector(s => s.sprints);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createError, setCreateError] = useState('');
  const [form, setForm] = useState({ name: '', sprintType: 'strict', startDate: '', endDate: '', capacity: '', description: '' });

  // Track which sprints have their feature list expanded in sidebar
  const [openSprints, setOpenSprints] = useState<Set<string>>(new Set());
  // Features per sprint
  const [sprintFeatures, setSprintFeatures] = useState<Record<string, any[]>>({});

  useEffect(() => {
    dispatch(fetchSprintsByProject({ projectId }));
  }, [dispatch, projectId]);

  // Auto-open the selected sprint's feature list
  useEffect(() => {
    if (selectedSprintId) {
      setOpenSprints(prev => {
        if (prev.has(selectedSprintId)) return prev;
        return new Set(prev).add(selectedSprintId);
      });
    }
  }, [selectedSprintId]);

  // Fetch features for a sprint when its list is opened
  const fetchSprintFeatures = useCallback(async (sprintId: string) => {
    try {
      const res = await featureAPI.getByProject(projectId);
      const all = Array.isArray(res.data.data) ? res.data.data : [];
      const forSprint = all.filter((f: any) => f.sprintId === sprintId);
      setSprintFeatures(prev => ({ ...prev, [sprintId]: forSprint }));
    } catch {
      setSprintFeatures(prev => ({ ...prev, [sprintId]: [] }));
    }
  }, [projectId]);

  useEffect(() => {
    for (const sid of openSprints) {
      if (!sprintFeatures[sid]) fetchSprintFeatures(sid);
    }
  }, [openSprints, fetchSprintFeatures]);

  // Per-sprint expanded features state (synced with kanban via CustomEvent + backend)
  const [expandedMap, setExpandedMap] = useState<Record<string, Set<string>>>({});

  // Load preferences from backend when a sprint's feature list is opened
  useEffect(() => {
    for (const sid of openSprints) {
      if (expandedMap[sid] === undefined) {
        api.get(`/sprints/${sid}/preferences`).then(res => {
          const data = res.data?.data || {};
          setExpandedMap(prev => ({ ...prev, [sid]: new Set(data.expandedFeatures || []) }));
          if (data.featureOrder?.length) setOrderMap(prev => ({ ...prev, [sid]: data.featureOrder }));
        }).catch(() => {
          setExpandedMap(prev => ({ ...prev, [sid]: new Set() }));
        });
      }
    }
  }, [openSprints]);

  // Per-sprint feature order
  const [orderMap, setOrderMap] = useState<Record<string, string[]>>({});

  const getExpandedFeatures = (sprintId: string): Set<string> => expandedMap[sprintId] || new Set();

  const toggleFeatureExpanded = (sid: string, featureId: string) => {
    const current = new Set(getExpandedFeatures(sid));
    if (current.has(featureId)) current.delete(featureId);
    else current.add(featureId);
    setExpandedMap(prev => ({ ...prev, [sid]: current }));
    // Notify kanban + persist
    const expanded = [...current];
    window.dispatchEvent(new CustomEvent('sprint-features-changed', { detail: { sprintId: sid, expanded } }));
    api.put(`/sprints/${sid}/preferences`, { expandedFeatures: expanded }).catch(() => {});
  };

  // Sync from kanban toggles
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sprintId && detail?.expanded) {
        setExpandedMap(prev => ({ ...prev, [detail.sprintId]: new Set(detail.expanded) }));
        if (detail.order) setOrderMap(prev => ({ ...prev, [detail.sprintId]: detail.order }));
      }
    };
    window.addEventListener('sprint-features-changed', handler);
    return () => window.removeEventListener('sprint-features-changed', handler);
  }, []);

  const toggleSprintOpen = (sprintId: string) => {
    setOpenSprints(prev => {
      const n = new Set(prev);
      if (n.has(sprintId)) n.delete(sprintId); else n.add(sprintId);
      return n;
    });
  };

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Sprint name is required';
    if (!form.startDate) e.startDate = 'Start date is required';
    if (form.endDate && form.startDate && new Date(form.endDate) <= new Date(form.startDate)) e.endDate = 'Must be after start date';
    return e;
  };

  const isValid = form.name.trim() && form.startDate;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    setCreating(true);
    setCreateError('');
    try {
      const res = await api.post('/sprints', {
        projectId,
        name: form.name.trim(),
        description: form.description || undefined,
        sprintType: form.sprintType,
        startDate: form.startDate,
        endDate: form.endDate || undefined,
        capacity: form.capacity ? Number(form.capacity) : undefined,
      });
      const created = res.data?.data || res.data?.sprint || res.data;
      setForm({ name: '', sprintType: 'strict', startDate: '', endDate: '', capacity: '', description: '' });
      setErrors({});
      setShowCreate(false);
      dispatch(fetchSprintsByProject({ projectId }));
      if (created?._id && onSelectSprint) {
        onSelectSprint(created);
      }
    } catch (err: any) {
      setCreateError(err.response?.data?.message || 'Failed to create sprint');
    }
    setCreating(false);
  };

  const clearError = (f: string) => { if (errors[f]) setErrors(prev => { const n = { ...prev }; delete n[f]; return n; }); };
  const inputCls = (field?: string) => `w-full border rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 bg-white transition-colors ${
    field && errors[field] ? 'border-red-300 focus:ring-red-500/30 focus:border-red-400' : 'border-gray-200 focus:ring-blue-500/30 focus:border-blue-400'
  }`;
  const labelCls = 'block text-xs font-medium text-gray-500 mb-1.5';

  return (
    <div className="flex flex-col h-full">
      {/* Section header */}
      <div className="flex items-center justify-between px-3 py-1.5 mt-1">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Sprints</span>
        <button
          onClick={() => { setShowCreate(true); setErrors({}); setCreateError(''); }}
          className="w-5 h-5 rounded flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          title="New sprint"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </button>
      </div>

      {/* Sprint list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-6"><div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
        ) : sprints.length === 0 && !showCreate ? (
          <div className="text-center py-6">
            <p className="text-xs text-gray-400">No sprints yet</p>
            <button onClick={() => setShowCreate(true)} className="text-xs text-blue-600 hover:text-blue-700 mt-1">Create one</button>
          </div>
        ) : (
          sprints.map(sprint => {
            const isSelected = selectedSprintId === sprint._id;
            const isOpen = openSprints.has(sprint._id);
            const features = sprintFeatures[sprint._id] || [];
            const expanded = getExpandedFeatures(sprint._id);

            return (
              <div key={sprint._id}>
                {/* Sprint row */}
                <div className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer text-sm hover:bg-gray-50 transition-colors group">
                  {/* Chevron to toggle feature list */}
                  <button
                    onClick={e => { e.stopPropagation(); toggleSprintOpen(sprint._id); }}
                    className="w-4 h-4 flex items-center justify-center shrink-0 text-gray-400 hover:text-gray-600"
                  >
                    <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {/* Sprint name — click to open kanban */}
                  <div
                    onClick={() => onSelectSprint?.(sprint)}
                    className={`flex items-center gap-2 flex-1 min-w-0 px-1 py-0.5 rounded ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${statusColors[sprint.status] || 'bg-gray-400'}`} />
                    <span className={`truncate flex-1 ${isSelected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{sprint.name}</span>
                    <span className="text-xs text-gray-400 capitalize shrink-0">{sprint.status}</span>
                  </div>
                </div>

                {/* Feature list under sprint */}
                {isOpen && (
                  <div className="ml-5 border-l border-gray-200">
                    {features.length === 0 ? (
                      <p className="text-[11px] text-gray-400 px-3 py-1.5">No features in this sprint</p>
                    ) : (
                      [...features].sort((a: any, b: any) => {
                        const order = orderMap[sprint._id] || [];
                        const ai = order.indexOf(a._id);
                        const bi = order.indexOf(b._id);
                        return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
                      }).map((f: any) => {
                        const isExpanded = expanded.has(f._id);
                        return (
                          <label
                            key={f._id}
                            className="flex items-center gap-2 px-3 py-1 cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isExpanded}
                              onChange={() => toggleFeatureExpanded(sprint._id, f._id)}
                              className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30 cursor-pointer"
                            />
                            <svg className="w-3 h-3 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                            </svg>
                            <span className={`text-[11px] truncate ${isExpanded ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                              {f.name}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create sprint modal */}
      {showCreate && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center p-4 sm:p-6 md:p-8" style={{ zIndex: 9999 }} onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <form onSubmit={handleCreate} className="flex flex-col flex-1 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">New Sprint</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Create a sprint to organize work into a time-boxed cycle</p>
                </div>
                <button type="button" onClick={() => setShowCreate(false)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
                {createError && (
                  <div className="bg-red-50 text-red-700 px-4 py-2.5 rounded-xl text-xs flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    {createError}
                  </div>
                )}

                <div>
                  <label className={labelCls}>Sprint Name *</label>
                  <input value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); clearError('name'); }} placeholder="Sprint 1 — Auth & Onboarding" className={inputCls('name')} />
                  {errors.name && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>{errors.name}</p>}
                </div>

                <div>
                  <label className={labelCls}>Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What's the goal of this sprint?" rows={2} className={`${inputCls()} resize-none`} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Type</label>
                    <select value={form.sprintType} onChange={e => setForm(f => ({ ...f, sprintType: e.target.value }))} className={inputCls()}>
                      <option value="strict">Strict</option>
                      <option value="fluid">Fluid</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Capacity</label>
                    <input type="number" min={0} value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} placeholder="e.g. 40" className={inputCls()} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Start Date *</label>
                    <input type="date" value={form.startDate} onChange={e => {
                      const val = e.target.value;
                      setForm(f => ({ ...f, startDate: val }));
                      clearError('startDate');
                      if (form.endDate && val && new Date(form.endDate) <= new Date(val)) setErrors(prev => ({ ...prev, endDate: 'Must be after start date' }));
                      else clearError('endDate');
                    }} className={inputCls('startDate')} />
                    {errors.startDate && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>{errors.startDate}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>End Date</label>
                    <input type="date" value={form.endDate} onChange={e => {
                      const val = e.target.value;
                      setForm(f => ({ ...f, endDate: val }));
                      if (val && form.startDate && new Date(val) <= new Date(form.startDate)) setErrors(prev => ({ ...prev, endDate: 'Must be after start date' }));
                      else clearError('endDate');
                    }} className={inputCls('endDate')} />
                    {errors.endDate && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>{errors.endDate}</p>}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
                <button type="button" onClick={() => setShowCreate(false)} className="px-5 py-2.5 text-sm text-gray-600 hover:text-gray-800 rounded-xl hover:bg-gray-100 transition-colors">Cancel</button>
                <button type="submit" disabled={creating || !isValid} className="bg-blue-600 text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors">
                  {creating ? 'Creating...' : 'Create Sprint'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SprintTree;
